// Shared panzoom + dots + transform wiring for canvas-style pages
// (/studios, /team-and-agents). Lifts the inline panzoom setup from
// canvas/pages/studios.js (around line 7060) into a single helper so
// non-project canvas pages don't have to copy the same boilerplate.
//
// What this owns:
//   - panzoom(scene) with sensible defaults + `beforeMouseDown` gate
//     so dragging a `[data-node-id]` child doesn't pan the canvas.
//   - Wheel handling: plain wheel → pan, ⌘/Ctrl+wheel → zoom.
//   - Safari trackpad pinch (gesturestart/gesturechange/gestureend) →
//     zoom through the same zoom-at-point path as ⌘/Ctrl+wheel, with
//     native PAGE zoom suppressed over the board only (never globally).
//   - Dots layer position + size update on every transform tick.
//   - `--canvas-scale` CSS var on the scene element so children can
//     counter-scale (overlays / handles that should stay viewport-sized).
//   - Idempotent attach (no double-binding on re-render).
//
// What this DOESN'T own (intentionally):
//   - Connection drawing — page-specific (different node shapes / data).
//   - Drag handlers per node — page-specific.
//   - Initial position + persistence — page picks its own storage key.
//   - GlobalScope mirroring — used to be wired here for the Zoombar but
//     keeping it page-side avoids cross-page leakage.
//
// Usage from a page's onRender:
//   import { initPanzoomBoard } from '../functions/initPanzoomBoard.js'
//   onRender: (el, s) => {
//     if (el.__pzBooted) return
//     el.__pzBooted = true
//     initPanzoomBoard({
//       getScene: () => el.MyBoard?.node,
//       getDots:  () => el.MyDots?.node,
//       initial:  { x: 40, y: 40, scale: 1 },
//       attachKey: '__myPagePanzoom',   // marker so re-render doesn't re-attach
//       onTransform: (t) => { window.__myPageScale = t.scale }
//     })
//   }
//
// Returns the panzoom instance (or null when scene wasn't ready yet —
// the helper auto-retries via requestAnimationFrame).

import panzoom from 'panzoom/dist/panzoom.js'
// Bare shared-library import, not a deep functions/canvasBackground.js
// subpath — a per-file deep specifier into @symbo-ls/workspace-shared cannot
// resolve through the platform's public importmap (tickets/execution-board.md
// §1, same fix as workspace/app.js). Deferred call/getter wrappers, not a
// module-scope destructure, per the a5d0a2aa barrel-cycle lesson.
import _wsShared from '@symbo-ls/workspace-shared'
const applyCanvasDotMetrics = (...args) => _wsShared.functions.applyCanvasDotMetrics(...args)
// Read through this getter, never as a bare `CANVAS_DOT_VISIBILITY_FLOOR` —
// the constant is NOT imported into this file, so a bare reference is an
// undeclared free variable: a ReferenceError on every pan/zoom repaint in the
// ordinary bundle, and an `unresolvable-scope-read` (`__scope.CANVAS_DOT_
// VISIBILITY_FLOOR`, guaranteed `undefined`) once frank serializes the board
// initializer. The getter existed for exactly this reason and the one call
// site had drifted off it.
const getCanvasDotVisibilityFloor = () => _wsShared.functions.CANVAS_DOT_VISIBILITY_FLOOR

const DEFAULT_BASE_DOT = 24

// Safari (macOS + iPadOS WKWebView) is the only engine that dispatches the
// non-standard gesturestart/gesturechange/gestureend events for trackpad
// pinches. Detected once at module load — on every other browser this stays
// false and the gesture wiring below never attaches (zero cost).
const HAS_GESTURE_EVENTS =
  typeof window !== 'undefined' &&
  (typeof window.GestureEvent !== 'undefined' || 'ongesturestart' in window)

export function initPanzoomBoard(opts = {}) {
  const {
    getScene,
    getDots,
    initial = { x: 40, y: 40, scale: 1 },
    attachKey = '__panzoomAttached',
    baseDotSize = DEFAULT_BASE_DOT,
    // Optional dot-grid overrides forwarded to applyCanvasDotMetrics —
    // `dotPeriod` stretches the zoom-recursion cycle (grid re-folds every
    // ×period of zoom; default 3), `dotInkDivisor` shrinks the dot radius
    // relative to spacing (default 40). Undefined → canvas-wide defaults,
    // so every existing board is untouched.
    dotPeriod,
    dotInkDivisor,
    beforeMouseDown,
    // Optional page-supplied wheel gate. When omitted, the default below
    // (plain wheel → pan, ⌘/Ctrl+wheel → zoom) is used. /studio supplies
    // its own so nested scrollable surfaces (dropdown panels) can own the
    // wheel before the canvas pan layer sees it.
    beforeWheel,
    onTransform,
    minZoom = 0.3,
    maxZoom = 2,
    zoomSpeed = 0.8,
    // Forwarded to panzoom(). /studio sets this true so panzoom doesn't
    // force tabindex="0" on the scene's owner element (the Studios root),
    // which otherwise steals focus from buttons on click. Defaults to
    // panzoom's own default (false) so existing callers are unaffected.
    disableKeyboardInteraction = false,
    maxRetries = 120,
    // Optional `{ canceled: boolean }` token — pass from the caller so
    // it can flip `.canceled = true` on page unmount and cancel the
    // pending RAF chain. Without this, getScene() returning null
    // forever would mean an indefinite per-frame retry loop.
    cancelToken
  } = opts
  // Internal retry counter — if the caller doesn't supply a token,
  // we still cap at maxRetries (~2s at 60fps) so a misconfigured
  // getScene path can't burn frames forever.
  const _attempts = opts._attempts || 0
  // The calling ELEMENT (every board invokes this via `el.call`, binding
  // `this`). Preserved across the RAF retry chain via opts — a plain
  // recursive call would drop it, and the live-cursors attach below needs it.
  const _callerEl =
    (this && typeof this.call === 'function' ? this : null) || opts.__callerEl || null

  if (cancelToken?.canceled) return null

  const scene = typeof getScene === 'function' ? getScene() : null
  if (!scene) {
    if (_attempts >= maxRetries) {
      if (typeof console !== 'undefined') {
        console.warn('[initPanzoomBoard] gave up after', maxRetries, 'frames — scene never mounted')
      }
      return null
    }
    requestAnimationFrame(() => {
      initPanzoomBoard({ ...opts, __callerEl: _callerEl, _attempts: _attempts + 1 })
    })
    return null
  }
  if (scene[attachKey]) return scene[attachKey]

  // The node gate — "don't pan when the press starts on a draggable node" — is
  // caller-supplied (each board names its own [data-*] node markers, e.g.
  // [data-ticket-id]/[data-col-header] on the tickets board) with a
  // [data-node-id] default. Resolve it ONCE so panzoom's mouse gate AND the
  // touch guard below share the SAME predicate — otherwise the touch guard only
  // recognizes [data-node-id] boards and pans the board while a finger drags a
  // ticket card / studios node / etc. Every board gate is target-only, so it's
  // safe to evaluate on a touchstart event.
  const nodeGate =
    typeof beforeMouseDown === 'function'
      ? beforeMouseDown
      : (e) => !!e.target.closest?.('[data-node-id]')

  // Shared between the default beforeWheel below and the Safari gesture
  // handlers further down: while a trackpad pinch is driving the zoom via
  // gesture events (base != null), the ctrl+wheel echo some Safari builds
  // emit for the SAME physical pinch must be swallowed — letting panzoom's
  // wheel zoom also run would double-apply every step. lastTs bounds the
  // gate so a gesture that never delivered its gestureend (element torn
  // down mid-pinch) can't mute ⌘/Ctrl+wheel zoom forever.
  const gestureState = { base: null, lastTs: 0 }

  // TDZ guard — panzoom() can fire wheel events synchronously inside
  // its constructor; split declaration so beforeWheel sees `undefined`
  // (not a ReferenceError) until pz binds. Mirrors studios.js.
  let pz
  pz = panzoom(scene, {
    minZoom,
    maxZoom,
    zoomSpeed,
    zoomDoubleClickSpeed: 1,
    disableKeyboardInteraction,
    // Allow page-supplied gate; default is "panzoom catches everything
    // except clicks on [data-node-id]" — same convention studios.js uses
    // and /team-and-agents already follows.
    beforeMouseDown: nodeGate,
    beforeWheel:
      typeof beforeWheel === 'function'
        ? (e) => beforeWheel(e, pz)
        : (e) => {
            if (!pz) return
            const isPinch = e.metaKey || e.ctrlKey
            if (isPinch) {
              // A Safari gesture (handlers below) is already driving this
              // pinch — swallow the wheel echo so the step isn't applied
              // twice. Returning true makes panzoom skip its wheel zoom;
              // preventDefault keeps the browser's own ctrl+wheel page
              // zoom out too.
              if (gestureState.base != null && Date.now() - gestureState.lastTs < 250) {
                e.preventDefault()
                return true
              }
              return false
            }
            e.preventDefault()
            const { x, y } = pz.getTransform()
            pz.moveTo(x - e.deltaX, y - e.deltaY)
            return true
          }
  })

  scene[attachKey] = pz

  // ── iPad / coarse-pointer touch hardening (once per scene) ───────────────
  // The canvas owns pan + pinch, so the browser must not scroll/zoom the PAGE
  // over it (touch-action: none). And panzoom drives its touch pan from a bare
  // single-finger touchstart with NO node gate — its beforeMouseDown gate above
  // is mouse-only — so a one-finger drag STARTING on a [data-node-id] would pan
  // the whole board, and panzoom's preventDefault would swallow the synthesized
  // tap-to-open click.
  //
  // Fix once, at the shared layer, for every board: a CAPTURE-phase touchstart
  // guard on the scene runs BEFORE panzoom's bubble listener (which binds to
  // scene.parentElement). When a single finger lands on a node, pause() panzoom
  // — releaseEvents() removes its touchstart listener before the bubble reaches
  // it, so it never starts a pan and never preventDefaults the tap — then
  // resume() when the touch ends. Per-board node drag handlers then own the
  // gesture. Multi-finger pinch is left alone so zoom still works over a node.
  if (!scene.__pzTouchHardened) {
    scene.__pzTouchHardened = true
    scene.style.touchAction = 'none'
    // Selection hardening (iPad, 2026-08-01 per Nika "when dragging around
    // on canvas prevent selections"): a touch-drag pan across board tiles
    // starts iOS TEXT SELECTION + the magnifier callout on any text it
    // crosses — the board is a gesture surface (Figma/Freeform model), not
    // prose, so selection is never a canvas gesture. Same rationale as the
    // home-grid rule shipped in wave 2. Owner-scoped (not global), and
    // editable descendants re-enable selection below so inline renames /
    // editors keep native caret + selection behavior.
    const pzOwnerSel = scene.parentElement || scene
    pzOwnerSel.style.userSelect = 'none'
    pzOwnerSel.style.webkitUserSelect = 'none'
    pzOwnerSel.style.webkitTouchCallout = 'none'
    for (const ed of scene.querySelectorAll('input, textarea, [contenteditable]')) {
      ed.style.userSelect = 'text'
      ed.style.webkitUserSelect = 'text'
    }
    // Late-mounted editables (rename inputs appear on demand): re-assert on
    // focusin — one delegated listener instead of observing the whole tree.
    pzOwnerSel.addEventListener(
      'focusin',
      (e) => {
        const t = e.target
        if (t && (t.matches?.('input, textarea, [contenteditable]') || t.isContentEditable)) {
          t.style.userSelect = 'text'
          t.style.webkitUserSelect = 'text'
        }
      },
      { passive: true }
    )
    const resumePz = () => {
      if (pz.isPaused()) pz.resume()
      document.removeEventListener('touchend', resumePz)
      document.removeEventListener('touchcancel', resumePz)
    }
    scene.addEventListener(
      'touchstart',
      (e) => {
        if (e.touches.length !== 1 || pz.isPaused()) return
        if (!nodeGate(e)) return
        pz.pause()
        document.addEventListener('touchend', resumePz)
        document.addEventListener('touchcancel', resumePz)
      },
      { capture: true, passive: true }
    )
  }

  // ── Safari trackpad pinch → board zoom (gesture events, once per scene) ──
  // On iPadOS with a Magic Keyboard trackpad (and on macOS Safari), pinch
  // never reaches the wheel path — WebKit dispatches the non-standard
  // gesturestart/gesturechange/gestureend events carrying a cumulative
  // e.scale, and when nothing preventDefaults them WKWebView performs native
  // PAGE zoom: the whole app scales instead of the board (touch-action does
  // not cover trackpad gestures). Handle them on panzoom's own input layer —
  // the owner element (scene.parentElement) all other board input
  // (wheel/mouse/touch) already binds to — so the suppression is scoped to
  // the board surface: gestures starting anywhere else in the app (settings
  // pages, text) keep Safari's default behavior, and no global
  // viewport/user-scalable hack is involved (browser zoom stays available
  // off-canvas — accessibility stays intact).
  //
  // Direct-touch pinches ALSO fire gesture events, but those already work:
  // panzoom's own touchmove handler drives them (and preventDefaults the
  // page zoom), so an active touch skips this path entirely to avoid
  // double-driving the transform. Listener teardown rides owner-node
  // teardown on page unmount — the same lifecycle the touch hardening above
  // and panzoom's own owner-bound listeners follow.
  if (HAS_GESTURE_EVENTS && !scene.__pzGestureAttached) {
    scene.__pzGestureAttached = true
    const pzOwner = scene.parentElement || scene

    // Zoom-at-point shared with the ⌘/Ctrl+wheel path. Wheel pinches zoom
    // via panzoom's INTERNAL wheel handler (the default beforeWheel above
    // returns false for them), which anchors at owner-relative offsets —
    // its getOffsetXY is clientXY − owner.getBoundingClientRect(). Feeding
    // pz.zoomAbs the same conversion drives the exact same zoomByRatio
    // engine (min/max clamped, same transform tick → dots/scale-var/DOM
    // writes), so both input paths stay on one zoom implementation instead
    // of duplicating the math here.
    const zoomAtClientPoint = (clientX, clientY, absScale) => {
      const rect = pzOwner.getBoundingClientRect()
      const cx = typeof clientX === 'number' ? clientX : rect.left + rect.width / 2
      const cy = typeof clientY === 'number' ? clientY : rect.top + rect.height / 2
      pz.zoomAbs(cx - rect.left, cy - rect.top, absScale)
    }

    let touchActive = 0
    const trackTouch = (e) => {
      touchActive = e.touches.length
    }
    const onGestureStart = (e) => {
      if (touchActive > 0) return
      e.preventDefault()
      gestureState.base = pz.getTransform().scale
      gestureState.lastTs = Date.now()
    }
    const onGestureChange = (e) => {
      if (gestureState.base == null) return
      e.preventDefault()
      gestureState.lastTs = Date.now()
      zoomAtClientPoint(e.clientX, e.clientY, gestureState.base * (e.scale || 1))
    }
    const onGestureEnd = (e) => {
      if (gestureState.base == null) return
      e.preventDefault()
      gestureState.base = null
    }
    pzOwner.addEventListener('touchstart', trackTouch, { passive: true })
    pzOwner.addEventListener('touchend', trackTouch, { passive: true })
    pzOwner.addEventListener('touchcancel', trackTouch, { passive: true })
    // Non-passive on purpose — preventDefault is the whole point (Safari
    // treats gesture listeners as non-passive by default, but be explicit).
    pzOwner.addEventListener('gesturestart', onGestureStart, { passive: false })
    pzOwner.addEventListener('gesturechange', onGestureChange, { passive: false })
    pzOwner.addEventListener('gestureend', onGestureEnd, { passive: false })

    // ── Native-shell trackpad pinch (symbols:native-pinch) ────────────────
    // In the Tauri iOS shell WKWebView delivers trackpad pinches to the
    // NATIVE gesture system only — no gesture events, no ctrl+wheel — so the
    // shell's UIPinchGestureRecognizer (native/ipad src-tauri lib.rs)
    // forwards phase/scale/centroid as this window CustomEvent. Drive the
    // SAME zoomAtClientPoint/gestureState machinery as the Safari gesture
    // path above. Browser sessions never receive the event — zero cost.
    // Containment is decided at gesture START (a pinch that begins over the
    // board keeps zooming while the centroid drifts, like native canvases);
    // direct-touch pinches skip via the same touchActive counter. The
    // listener is window-level (the event targets no node), so it self-
    // prunes when this board's owner leaves the DOM.
    let nativeIgnored = false
    const onNativePinch = (e) => {
      if (!pzOwner.isConnected) {
        window.removeEventListener('symbols:native-pinch', onNativePinch)
        return
      }
      const d = e.detail || {}
      if (d.phase === 'start') {
        nativeIgnored =
          touchActive > 0 || !pzOwner.contains(document.elementFromPoint(d.x || 0, d.y || 0))
        if (nativeIgnored) return
        gestureState.base = pz.getTransform().scale
        gestureState.lastTs = Date.now()
        return
      }
      if (nativeIgnored) {
        if (d.phase === 'end' || d.phase === 'cancel') nativeIgnored = false
        return
      }
      if (gestureState.base == null) return
      if (d.phase === 'change') {
        gestureState.lastTs = Date.now()
        zoomAtClientPoint(d.x, d.y, gestureState.base * (d.scale || 1))
        return
      }
      if (d.phase === 'end' || d.phase === 'cancel') {
        gestureState.base = null
      }
    }
    window.addEventListener('symbols:native-pinch', onNativePinch)
  }

  // Size/position only — both theme-agnostic, so stamping them inline is
  // safe. The dots' backgroundImage is owned by CanvasDots' '@dark'/'@light'
  // CSS; never write it inline here (inline would freeze the theme).
  const updateDots = () => {
    const dots = typeof getDots === 'function' ? getDots() : null
    if (!dots) return
    const t = pz.getTransform()
    // Shared dot-grid invariant (canvasBackground.canvasDotMetrics): spacing
    // folds into a bounded band and the dot radius tracks it, so the texture
    // reads identically at every zoom. The old `size = scale * baseDotSize`
    // was unbounded in BOTH directions — at scale 3 it drew a 0.6px dot on
    // 72px spacing (~0.02% ink, effectively invisible) and far out it packed
    // the dots into a solid wash. Same defect the project canvas hit as
    // "sometimes during scroll dots disappear" (2026-08-08), fixed once here
    // rather than per board.
    // minRadius: CANVAS_DOT_VISIBILITY_FLOOR — CODEX-V8 row 3 (2026-08-13 QA):
    // this helper's own DEFAULT_BASE_DOT (24) combined with the canvas-wide
    // CANVAS_DOT_INK_DIVISOR (40) folds down to a mathematically-correct-but-
    // invisible 0.6px radius at scale 1 (the exact seed every board booted
    // through this helper uses), confirmed via a controlled screenshot A/B/C
    // — 0.6px produced zero visible dots in three crop regions, 1px was
    // barely visible, 4px clearly visible. The ink-density ratio formula
    // (canvasDotMetrics, canvasDotMetrics.test.js) stays mathematically pure
    // — this floor is opt-in via applyCanvasDotMetrics' `minRadius` and only
    // ever raises a too-small radius, never lowers a healthy one.
    applyCanvasDotMetrics(dots, t.scale, t.x, t.y, baseDotSize, {
      period: dotPeriod,
      inkDivisor: dotInkDivisor,
      minRadius: getCanvasDotVisibilityFloor()
    })
  }

  const writeScaleVar = () => {
    const t = pz.getTransform()
    scene.style.setProperty('--canvas-scale', String(t.scale))
  }

  // DOMQL v3.14's CSS_PROPS registry doesn't include `transform` /
  // `transformOrigin`, so panzoom-seeded values won't reach the DOM on
  // first paint unless we write them directly to scene.style. Mirrors
  // studios.js (~line 7194) where the same write is done via
  // `el.Board.update({ style: { transform } })`. Without this, panzoom
  // holds the offset/scale internally but the board stays at identity
  // until the first user wheel/drag triggers a transform write.
  const writeTransform = () => {
    const t = pz.getTransform()
    scene.style.transformOrigin = '0 0'
    scene.style.transform = `matrix(${t.scale}, 0, 0, ${t.scale}, ${t.x}, ${t.y})`
  }

  pz.on('transform', () => {
    updateDots()
    writeScaleVar()
    writeTransform()
    if (typeof onTransform === 'function') onTransform(pz.getTransform())
  })

  // Seed initial transform — `pz.moveTo` + `zoomAbs` together so the
  // first paint isn't pinned to (0, 0, 1).
  if (initial) {
    if (typeof initial.scale === 'number') pz.zoomAbs(0, 0, initial.scale)
    if (initial.x != null || initial.y != null) pz.moveTo(initial.x || 0, initial.y || 0)
  }
  updateDots()
  writeScaleVar()
  writeTransform()

  // ── Offscreen-content auto-refit (phones / small viewports) ─────────────
  // Two board classes shipped desktop-shaped cameras that leave a phone
  // viewport EMPTY (iPhone audit 2026-08-02): tickets seeds a hardcoded
  // `initial.x: 370` (clears the DESKTOP filters rail — at 390px that pushes
  // every column past the right edge), and studios restores tile positions
  // dragged on desktop (content bbox can start ~1000px out with the camera
  // at origin). Rather than patching each board's seed, detect the failure
  // itself: once children exist, if the content bbox does not intersect the
  // owner viewport, pan the camera so the content's near corner lands at a
  // small margin. Runs only until first user input (their camera, their
  // rules), only pans (never rescales), and no-ops on every board whose
  // content is already visible — desktop stays byte-identical.
  {
    const pzOwnerFit = scene.parentElement || scene
    let userDrove = false
    const markDriven = () => {
      userDrove = true
    }
    pzOwnerFit.addEventListener('pointerdown', markDriven, { passive: true, once: true })
    pzOwnerFit.addEventListener('wheel', markDriven, { passive: true, once: true })
    let attempts = 0
    const tryFit = () => {
      if (userDrove || !pzOwnerFit.isConnected) return
      if (attempts++ > 120) return // ~2s of frames — content never arrived
      const kids = scene.children
      if (!kids.length) {
        requestAnimationFrame(tryFit)
        return
      }
      let L = Infinity
      let T = Infinity
      let R = -Infinity
      let B = -Infinity
      const cap = Math.min(kids.length, 200)
      for (let i = 0; i < cap; i++) {
        const r = kids[i].getBoundingClientRect()
        if (!r.width && !r.height) continue
        if (r.left < L) L = r.left
        if (r.top < T) T = r.top
        if (r.right > R) R = r.right
        if (r.bottom > B) B = r.bottom
      }
      if (L === Infinity) {
        requestAnimationFrame(tryFit)
        return
      }
      const o = pzOwnerFit.getBoundingClientRect()
      // Per-axis visible slice. A bare intersection test is too lenient —
      // the live tickets board showed a 16px sliver of a 2547px-wide board
      // "intersecting" a 390px phone viewport while reading as blank. Refit
      // an axis when under 80px of it is visible AND there is meaningfully
      // more content hidden beyond (so a small board that simply IS narrow
      // never gets yanked to the margin).
      const visW = Math.max(0, Math.min(R, o.right) - Math.max(L, o.left))
      const visH = Math.max(0, Math.min(B, o.bottom) - Math.max(T, o.top))
      const needX = visW < 80 && R - L - visW > 40
      const needY = visH < 80 && B - T - visH > 40
      if (!needX && !needY) return
      const margin = 24
      const t = pz.getTransform()
      const dx = needX ? o.left + margin - L : 0
      const dy = needY ? o.top + margin - T : 0
      pz.moveTo(t.x + dx, t.y + dy)
      updateDots()
      writeScaleVar()
      writeTransform()
    }
    requestAnimationFrame(tryFit)
  }

  // Expose a manual transform flush. DOMQL v3.14 never writes `transform`
  // itself (see writeTransform above), and a programmatic `pz.moveTo` only
  // reaches the DOM through the panzoom `transform` event — which does NOT
  // fire on the home AgentsWidget embed (the board stays pinned to the seed).
  // Callers that move the camera in code (the /my-agents + home-embed
  // fit-to-visible) call this to force the write; redundant-but-harmless on
  // the routes where the event fires normally.
  pz.applyBoardTransform = () => {
    updateDots()
    writeScaleVar()
    writeTransform()
    // A manual flush IS a transform change — notify onTransform subscribers so
    // consumers driven off it stay in sync on the embed / fit paths where
    // panzoom's own `transform` event never fires (e.g. team-and-agents'
    // viewport-windowing recompute, and the globalScope scale mirror). Guarded
    // so a caller without onTransform is unaffected; idempotent for the scale
    // mirrors that already run on every real transform event.
    if (typeof onTransform === 'function') onTransform(pz.getTransform())
  }

  // ── Live cursors (shared across every panzoom board) ─────────────────────
  // The project canvas has always shown collaborators' cursors
  // (CollabService → root.clients → ViewSvitcher OverlayPeers); this extends
  // the same functionality to EVERY board that boots through this helper —
  // tickets board/epics, /studios, /team-and-agents, /files, /infra, the
  // analytics map — with zero per-page wiring. Transport is the /user-socket
  // presence relay (shared/functions/liveCursors.js), scoped per
  // workspace + path. Every call site invokes this helper via
  // `el.call('initPanzoomBoard', …)`, so `this` is the calling element;
  // a bare-import caller just skips cursors.
  if (_callerEl) {
    try {
      _attachLiveCursors(_callerEl, scene, pz, cancelToken)
    } catch (err) {
      console.warn('[initPanzoomBoard] live cursors attach failed:', err?.message || err)
    }
  }

  return pz
}

// Imperative cursor layer INSIDE the panzoom scene — peers are positioned in
// CONTENT coordinates so the board transform places them for free; sprites
// counter-scale so they stay viewport-sized. Same imperative altitude as the
// dots/transform plumbing above: peer moves at ~20/s must never touch
// reactive state.
const _cursorHue = (key) => {
  let h = 0
  const str = String(key || '')
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) % 360
  return h
}

function _attachLiveCursors(el, scene, pz, cancelToken) {
  if (!el || !scene || scene.__liveCursorsAttached) return
  scene.__liveCursorsAttached = true

  const layer = document.createElement('div')
  layer.className = 'live-cursors-layer'
  layer.style.cssText =
    'position:absolute;left:0;top:0;width:0;height:0;overflow:visible;pointer-events:none;z-index:80;'
  scene.appendChild(layer)

  const nodes = new Map() // peerId -> { node, img, label }
  const last = new Map() // peerId -> peer (for zoom-time repaint)

  const _scale = () => parseFloat(scene.style.getPropertyValue('--canvas-scale')) || 1

  const _place = (entry, peer) => {
    entry.node.style.transform = `translate3d(${peer.x}px, ${peer.y}px, 0) scale(${1 / _scale()})`
  }

  const paint = (peerId, peer) => {
    if (!peer) {
      const entry = nodes.get(peerId)
      if (entry) {
        entry.node.remove()
        nodes.delete(peerId)
      }
      last.delete(peerId)
      return
    }
    let entry = nodes.get(peerId)
    if (!entry) {
      const hue = _cursorHue(peer.userKey || peerId)
      const node = document.createElement('div')
      node.style.cssText =
        'position:absolute;left:0;top:0;display:flex;align-items:flex-start;gap:4px;' +
        'transform-origin:0 0;transition:transform 80ms linear;will-change:transform;'
      // Pointer glyph + avatar + name chip. Peer-controlled text goes in via
      // textContent ONLY (never markup). The glyph is the brand sprite's
      // `cursor` symbol — the `<use>` markup the Icon atom renders — tinted
      // through currentColor; the viewBox crops the 24-unit symbol to its
      // pointer, so the tip sits on the peer's point.
      node.innerHTML =
        `<svg width="13" height="20" viewBox="5.5 2.5 13 20" aria-hidden="true" style="flex-shrink:0;color:hsl(${hue} 75% 55%);filter:drop-shadow(0 1px 2px rgba(0,0,0,0.35))" stroke="white" stroke-width="1"><use xlink:href="#cursor" width="24" height="24"/></svg>` +
        `<span style="display:flex;align-items:center;gap:5px;margin-top:10px;padding:2px 7px 2px 3px;border-radius:999px;background:hsl(${hue} 75% 45%);box-shadow:0 1px 4px rgba(0,0,0,0.3)">` +
        `<img alt="" style="width:16px;height:16px;border-radius:50%;display:block"/>` +
        `<span style="font-size:11px;font-weight:700;color:#fff;white-space:nowrap;line-height:1.4"></span>` +
        '</span>'
      layer.appendChild(node)
      entry = {
        node,
        img: node.querySelector('img'),
        label: node.querySelector('span span')
      }
      nodes.set(peerId, entry)
    }
    if (peer.name && entry.label.textContent !== peer.name) entry.label.textContent = peer.name
    if (peer.userKey && !entry.img.__src) {
      entry.img.__src = true
      try {
        entry.img.src = el.call('avatarUrlFor', peer.userKey) || ''
      } catch (_) {
        entry.img.style.display = 'none'
      }
    }
    last.set(peerId, peer)
    _place(entry, peer)
  }

  // `let` + a rejoin path: the channel scope derives from root.currentPath,
  // which canonicalizes ASYNC during boot ('/' → '/w/<ws>' → the page path) —
  // a join made mid-boot lands on a stale scope and would never hear the
  // peers on the real one. The lifecheck below re-joins when the surface
  // scope drifts (also covers the signed-out-at-attach case: scope null →
  // joins once auth lands).
  let ch = el.call('liveCursorsJoin', paint) || { scope: null, send: () => {}, leave: () => {} }

  // Zoom changes must counter-scale existing sprites even when no peer moved.
  pz.on('transform', () => {
    for (const [peerId, peer] of last) {
      const entry = nodes.get(peerId)
      if (entry) _place(entry, peer)
    }
  })

  // Broadcast my pointer in CONTENT coordinates. The scene's bounding rect is
  // the transformed content box, so (client - rect) / scale needs no matrix
  // math and no panzoom internals.
  const vp = scene.parentElement || scene
  const onMove = (e) => {
    const rect = scene.getBoundingClientRect()
    const scale = rect.width / (scene.offsetWidth || 1)
    ch.send({
      x: (e.clientX - rect.left) / scale,
      y: (e.clientY - rect.top) / scale
    })
  }
  const onLeave = () => ch.send({ gone: true })
  vp.addEventListener('pointermove', onMove, { passive: true })
  vp.addEventListener('pointerleave', onLeave, { passive: true })

  // Lifecycle without per-page wiring: when the board unmounts (scene leaves
  // the DOM) or the page cancels its panzoom boot, release everything —
  // the channel ref-count closes the socket with the last board. The same
  // tick heals scope drift: if the surface scope the root NOW resolves to
  // differs from the channel's (mid-boot path canonicalization, workspace
  // switch), leave and re-join so the overlay always listens on the scope
  // peers are actually sending to.
  const lifecheck = setInterval(() => {
    if (cancelToken?.canceled || !scene.isConnected) {
      clearInterval(lifecheck)
      vp.removeEventListener('pointermove', onMove)
      vp.removeEventListener('pointerleave', onLeave)
      try {
        ch.leave()
      } catch (_) {}
      layer.remove()
      scene.__liveCursorsAttached = false
      return
    }
    const want = el.call('liveCursorsScope')
    if (want && want !== ch.scope) {
      try {
        ch.leave()
      } catch (_) {}
      for (const entry of nodes.values()) entry.node.remove()
      nodes.clear()
      last.clear()
      ch = el.call('liveCursorsJoin', paint) || ch
    }
  }, 2000)
}
